import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Bot, Pencil, X } from 'lucide-react';
import { Portal } from '../components/common/Portal';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type SubscriberData = {
  date: string;
  new_subscribers: number;
  total_subscribers: number;
};

type ViewData = {
  date: string;
  class_views: number;
  blueprint_views: number;
};

type AnalyticsData = {
  totalSubscribers: number;
  newSubscribersThisMonth: number;
  subscriberTrend: SubscriberData[];
  viewsTrend: ViewData[];
  mostViewedClasses: Array<{
    id: string;
    title: string;
    views: number;
  }>;
  mostViewedBlueprints: Array<{
    id: string;
    title: string;
    views: number;
  }>;
};

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSubscribers: 0,
    newSubscribersThisMonth: 0,
    subscriberTrend: [],
    viewsTrend: [],
    mostViewedClasses: [],
    mostViewedBlueprints: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // AI System Prompt modal state
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptId, setPromptId] = useState<string | null>(null);
  const [promptText, setPromptText] = useState('');
  const [originalPromptText, setOriginalPromptText] = useState('');
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);

  useEffect(() => {
    fetchAnalytics();
    
    // Add window resize listener for responsive design
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      
      // Calculate date range based on selected time range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case '7d':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(startDate.getDate() - 90);
          break;
      }
      
      // Start and end dates now prepared for queries

      // 1. Get total subscribers
      const { data: subscribers, error: subscribersError } = await supabase
        .from('profiles')
        .select('count')
        .eq('is_subscribed', true);

      if (subscribersError) throw subscribersError;

      // 2. Get new subscribers this month
      const firstDayOfMonth = new Date();
      firstDayOfMonth.setDate(1);
      firstDayOfMonth.setHours(0, 0, 0, 0);

      const { data: newSubscribers, error: newSubError } = await supabase
        .from('profiles')
        .select('count')
        .eq('is_subscribed', true)
        .gte('created_at', firstDayOfMonth.toISOString());

      if (newSubError) throw newSubError;

      // 3. Get daily stats for trends
      const { data: dailyStats, error: dailyStatsError } = await supabase
        .from('daily_stats')
        .select('date, new_subscribers, total_subscribers, class_views, blueprint_views')
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', endDate.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (dailyStatsError) throw dailyStatsError;
      
      // Format data for charts
      const subscriberTrend = dailyStats?.map(day => ({
        date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        new_subscribers: day.new_subscribers,
        total_subscribers: day.total_subscribers
      })) || [];
      
      const viewsTrend = dailyStats?.map(day => ({
        date: new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        class_views: day.class_views,
        blueprint_views: day.blueprint_views
      })) || [];

      // 4. Get most viewed classes
      const { data: classes, error: classesError } = await supabase
        .from('classes')
        .select('id, title, views')
        .order('views', { ascending: false })
        .limit(5);

      if (classesError) throw classesError;

      // 5. Get most viewed blueprints
      const { data: blueprints, error: blueprintsError } = await supabase
        .from('blueprints')
        .select('id, title, views')
        .order('views', { ascending: false })
        .limit(5);

      if (blueprintsError) throw blueprintsError;

      setAnalytics({
        totalSubscribers: subscribers?.[0]?.count || 0,
        newSubscribersThisMonth: newSubscribers?.[0]?.count || 0,
        subscriberTrend,
        viewsTrend,
        mostViewedClasses: classes || [],
        mostViewedBlueprints: blueprints || []
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Modal helpers
  const fetchLatestPrompt = async () => {
    setPromptLoading(true);
    try {
      const { data, error } = await supabase
        .from('ai_system_prompts')
        .select('id, prompt')
        .order('updated_at', { ascending: false })
        .limit(1)
        .single();
      if (error) throw error;
      const row = data as { id: string; prompt: string };
      setPromptId(row.id);
      setPromptText(row.prompt);
      setOriginalPromptText(row.prompt);
      setIsEditingPrompt(false);
    } catch (e) {
      console.error('Failed to load system prompt:', e);
    } finally {
      setPromptLoading(false);
    }
  };

  const openPromptModal = async () => {
    setIsPromptModalOpen(true);
    await fetchLatestPrompt();
  };

  const closePromptModal = () => {
    const hasUnsaved = promptText !== originalPromptText;
    if (hasUnsaved) {
      const confirmClose = window.confirm('You have unsaved changes. Close without saving?');
      if (!confirmClose) return;
    }
    setIsPromptModalOpen(false);
  };

  const savePrompt = async () => {
    if (!promptId) return;
    try {
      setPromptSaving(true);
      const { data, error } = await supabase
        .from('ai_system_prompts')
        .update({ prompt: promptText })
        .eq('id', promptId)
        .select('id, prompt')
        .single();
      if (error) throw error;
      const row = data as { id: string; prompt: string };
      setOriginalPromptText(row.prompt);
      setIsEditingPrompt(false);
      setIsPromptModalOpen(false);
    } catch (e) {
      console.error('Failed to save system prompt:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setPromptSaving(false);
    }
  };

  // Chart configurations
  const subscriberChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          color: 'white'
        }
      },
      title: {
        display: false
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)'
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        ticks: { color: 'rgba(255, 255, 255, 0.7)' }
      },
      y: {
        grid: { color: 'rgba(255, 255, 255, 0.1)' },
        ticks: { color: 'rgba(255, 255, 255, 0.7)' }
      }
    }
  };

  const viewsChartOptions = {
    ...subscriberChartOptions
  };

  // Chart data for subscribers
  const subscriberChartData = {
    labels: analytics.subscriberTrend.map(day => day.date),
    datasets: [
      {
        label: 'New Subscribers',
        data: analytics.subscriberTrend.map(day => day.new_subscribers),
        borderColor: 'rgb(147, 51, 234)',
        backgroundColor: 'rgba(147, 51, 234, 0.5)',
        tension: 0.3
      },
      {
        label: 'Total Subscribers',
        data: analytics.subscriberTrend.map(day => day.total_subscribers),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        tension: 0.3
      }
    ]
  };

  // Chart data for content views
  const viewsChartData = {
    labels: analytics.viewsTrend.map(day => day.date),
    datasets: [
      {
        label: 'Class Views',
        data: analytics.viewsTrend.map(day => day.class_views),
        backgroundColor: 'rgba(52, 211, 153, 0.7)',
        borderColor: 'rgb(52, 211, 153)'
      },
      {
        label: 'Blueprint Views',
        data: analytics.viewsTrend.map(day => day.blueprint_views),
        backgroundColor: 'rgba(45, 212, 191, 0.7)',
        borderColor: 'rgb(45, 212, 191)'
      }
    ]
  };

  // Top content distribution chart
  const distributionData = {
    labels: [...analytics.mostViewedClasses.slice(0, 3).map(c => 
      windowWidth < 640 && c.title.length > 15 ? `${c.title.substring(0, 15)}...` : c.title
    ), ...analytics.mostViewedBlueprints.slice(0, 3).map(b => 
      windowWidth < 640 && b.title.length > 15 ? `${b.title.substring(0, 15)}...` : b.title
    )],
    datasets: [
      {
        data: [
          ...analytics.mostViewedClasses.slice(0, 3).map(c => c.views),
          ...analytics.mostViewedBlueprints.slice(0, 3).map(b => b.views)
        ],
        backgroundColor: [
          'rgba(52, 211, 153, 0.7)',
          'rgba(59, 130, 246, 0.7)',
          'rgba(147, 51, 234, 0.7)',
          'rgba(45, 212, 191, 0.7)',
          'rgba(249, 115, 22, 0.7)',
          'rgba(236, 72, 153, 0.7)'
        ],
        borderColor: [
          'rgb(52, 211, 153)',
          'rgb(59, 130, 246)',
          'rgb(147, 51, 234)',
          'rgb(45, 212, 191)',
          'rgb(249, 115, 22)',
          'rgb(236, 72, 153)'
        ],
        borderWidth: 1
      }
    ]
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center min-h-[60vh]">
        <div className="text-white text-xl flex items-center">
          <svg className="animate-spin h-8 w-8 mr-3 text-purple-500" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading analytics data...
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-white mb-2">Admin Dashboard</h1>
        
        {/* Time range selector */}
        <div className="flex space-x-2 bg-gray-800 p-1 rounded-lg">
          <button 
            onClick={() => setTimeRange('7d')} 
            className={`px-4 py-2 rounded-md ${timeRange === '7d' ? 'bg-purple-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            7 Days
          </button>
          <button 
            onClick={() => setTimeRange('30d')} 
            className={`px-4 py-2 rounded-md ${timeRange === '30d' ? 'bg-purple-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            30 Days
          </button>
          <button 
            onClick={() => setTimeRange('90d')} 
            className={`px-4 py-2 rounded-md ${timeRange === '90d' ? 'bg-purple-700 text-white' : 'text-gray-300 hover:bg-gray-700'}`}
          >
            90 Days
          </button>
        </div>
      </div>
      
      {/* Subscriber Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 border border-purple-500/50 shadow-lg hover:border-purple-400/60 transition-colors shadow-purple-900/20">
          <h2 className="text-xl font-semibold text-purple-400 mb-2">Total Subscribers</h2>
          <div className="flex items-end gap-2">
            <p className="text-4xl font-bold text-white">{analytics.totalSubscribers}</p>
            <p className="text-sm text-purple-300 pb-1">paid users</p>
          </div>
        </div>
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 border border-blue-500/50 shadow-lg hover:border-blue-400/60 transition-colors shadow-blue-900/20">
          <h2 className="text-xl font-semibold text-blue-400 mb-2">New Subscribers This Month</h2>
          <div className="flex items-end gap-2">
            <p className="text-4xl font-bold text-white">{analytics.newSubscribersThisMonth}</p>
            <p className="text-sm text-blue-300 pb-1">new sign-ups</p>
          </div>
        </div>
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 border border-cyan-500/50 shadow-lg hover:border-cyan-400/60 transition-colors shadow-cyan-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/15 border border-cyan-400/20 flex items-center justify-center">
                <Bot className="w-6 h-6 text-cyan-300" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-cyan-400">AI Agent</h2>
                <p className="text-sm text-cyan-200/70">Configure system prompt</p>
              </div>
            </div>
            <button
              onClick={openPromptModal}
              className="px-4 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-500 transition-colors"
            >
              Configure
            </button>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscriber Growth Chart */}
        <div className="col-span-2 bg-gray-800/80 backdrop-blur rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-purple-400 mb-4">Subscriber Growth</h2>
          <div className="h-80">
            <Line data={subscriberChartData} options={subscriberChartOptions} />
          </div>
        </div>
        
        {/* Content Views Distribution */}
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-cyan-400 mb-4">Top Content Distribution</h2>
          <div className="h-80">
            <Doughnut data={distributionData} options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  position: 'bottom',
                  labels: { color: 'white' }
                }
              }
            }} />
          </div>
        </div>

        {/* Content Views Chart */}
        <div className="col-span-3 bg-gray-800/80 backdrop-blur rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-emerald-400 mb-4">Content Views Trend</h2>
          <div className="h-80">
            <Bar data={viewsChartData} options={viewsChartOptions} />
          </div>
        </div>
      </div>

      {/* Most Viewed Content */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Most Viewed Classes */}
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-green-400 mb-4">Most Viewed Classes</h2>
          <div className="space-y-4">
            {analytics.mostViewedClasses.map((classItem, index) => (
              <div 
                key={classItem.id} 
                className="flex justify-between items-center p-4 bg-gray-700/60 rounded-lg hover:bg-gray-600/80 transition-colors"
              >
                <div className="flex items-center flex-1 min-w-0 mr-2">
                  <span className="text-xl font-bold text-white/50 mr-3 shrink-0">{index + 1}</span>
                  <span className="text-white truncate">
                    {window.innerWidth < 640 && classItem.title.length > 20 
                      ? `${classItem.title.substring(0, 20)}...` 
                      : classItem.title}
                  </span>
                </div>
                <span className="text-green-400 font-semibold px-3 py-1 bg-green-400/10 rounded-full whitespace-nowrap">
                  {classItem.views} {windowWidth >= 640 ? 'views' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Most Viewed Blueprints */}
        <div className="bg-gray-800/80 backdrop-blur rounded-xl p-6 shadow-lg">
          <h2 className="text-xl font-semibold text-cyan-400 mb-4">Most Viewed Blueprints</h2>
          <div className="space-y-4">
            {analytics.mostViewedBlueprints.map((blueprint, index) => (
              <div 
                key={blueprint.id} 
                className="flex justify-between items-center p-4 bg-gray-700/60 rounded-lg hover:bg-gray-600/80 transition-colors"
              >
                <div className="flex items-center flex-1 min-w-0 mr-2">
                  <span className="text-xl font-bold text-white/50 mr-3 shrink-0">{index + 1}</span>
                  <span className="text-white truncate">
                    {window.innerWidth < 640 && blueprint.title.length > 20 
                      ? `${blueprint.title.substring(0, 20)}...` 
                      : blueprint.title}
                  </span>
                </div>
                <span className="text-cyan-400 font-semibold px-3 py-1 bg-cyan-400/10 rounded-full whitespace-nowrap">
                  {blueprint.views} {windowWidth >= 640 ? 'views' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* AI Prompt Configure Modal */}
      {isPromptModalOpen && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closePromptModal} />
            <div className="relative z-10 w-[95%] md:w-3/4 lg:w-2/3 max-h-[85vh] rounded-2xl bg-gray-900/95 border border-white/10 shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-cyan-500/15 border border-cyan-400/20 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-cyan-300" />
                  </div>
                  <h3 className="text-white font-semibold">AI Agent System Prompt</h3>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setIsEditingPrompt((v) => !v)}
                    className={`p-2 rounded-md border transition-colors ${isEditingPrompt ? 'bg-yellow-600/20 border-yellow-400/30 text-yellow-200' : 'bg-gray-800/60 border-white/10 text-gray-200 hover:bg-gray-700/60'}`}
                    title={isEditingPrompt ? 'Editing enabled' : 'Enable editing'}
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={closePromptModal}
                    className="p-2 rounded-md bg-gray-800/60 border border-white/10 text-gray-200 hover:bg-gray-700/60"
                    title="Close"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-4">
                {promptLoading ? (
                  <div className="text-gray-300">Loading prompt...</div>
                ) : (
                  <textarea
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    readOnly={!isEditingPrompt}
                    className="w-full min-h-[50vh] max-h-[60vh] p-3 rounded-lg bg-gray-900 text-gray-100 border border-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 disabled:opacity-70 disabled:cursor-not-allowed"
                    placeholder="Enter system prompt..."
                  />
                )}
              </div>
              <div className="flex items-center justify-end gap-3 p-4 border-t border-white/10">
                <button
                  onClick={closePromptModal}
                  className="px-4 py-2 rounded-md bg-gray-800/60 text-gray-200 border border-white/10 hover:bg-gray-700/60"
                >
                  Close
                </button>
                <button
                  onClick={savePrompt}
                  disabled={promptSaving || promptText === originalPromptText}
                  className="px-4 py-2 rounded-md bg-cyan-600 text-white hover:bg-cyan-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {promptSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
